import PropTypes from "prop-types";

/**
 * Universal Icon component that works identically on all platforms
 * Uses SVG for crisp rendering at all sizes and full accessibility support
 */
export const Icon = ({
  name,
  size = 24,
  color = "currentColor",
  className = "",
  ...props
}) => {
  // Icon definitions - Add more icons as needed
  const icons = {
    // Navigation
    chevronLeft: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    ),
    chevronRight: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    ),
    chevronUp: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
    ),
    chevronDown: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    ),

    // Actions
    close: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    ),
    plus: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
      />
    ),
    minus: <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />,

    // Status
    check: (
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    ),
    warning: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
      />
    ),
    error: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),
    info: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    ),

    // Shopping
    cart: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01"
      />
    ),

    // Download
    download: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z"
      />
    ),

    // Menu
    menu: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6h16M4 12h16M4 18h16"
      />
    ),

    // Search
    search: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    ),
  };

  const iconPath = icons[name];

  if (!iconPath) {
    console.warn(
      `Icon "${name}" not found. Available icons:`,
      Object.keys(icons).join(", "),
    );
    return null;
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      className={className}
      role="img"
      aria-hidden={props["aria-label"] ? "false" : "true"}
      {...props}
    >
      {iconPath}
    </svg>
  );
};

Icon.propTypes = {
  name: PropTypes.oneOf([
    "chevronLeft",
    "chevronRight",
    "chevronUp",
    "chevronDown",
    "close",
    "plus",
    "minus",
    "check",
    "warning",
    "error",
    "info",
    "cart",
    "download",
    "menu",
    "search",
  ]).isRequired,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
};

export default Icon;
